import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FileText,
  Percent,
  Wallet,
  Users,
  ArrowLeft,
  Loader2,
  Sparkles,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { api } from "@/lib/api";
import { toast } from "sonner";

interface LoanTypeForm {
  id?: number;
  name: string;
  code: string;
  description: string;
  interestRate: number;
  interestType: "flat" | "reducing";
  maxAmount: number;
  minAmount: number;
  maxTermMonths: number;
  processingFee: number;
  insuranceFee: number;
  penaltyRate: number;
  requiredSharesMultiplier: number;
  requiredGuarantors: number;
  isActive: boolean;
}

const defaultForm: LoanTypeForm = {
  name: "",
  code: "",
  description: "",
  interestRate: 12,
  interestType: "flat",
  maxAmount: 5000000,
  minAmount: 50000,
  maxTermMonths: 12,
  processingFee: 0,
  insuranceFee: 0,
  penaltyRate: 2,
  requiredSharesMultiplier: 2,
  requiredGuarantors: 2,
  isActive: true,
};

interface Template {
  label: string;
  values: Partial<LoanTypeForm>;
}

const TEMPLATES: Template[] = [
  {
    label: "Emergency Loan",
    values: {
      name: "Emergency Loan",
      code: "EMG-001",
      description:
        "Quick access to funds for unexpected personal emergencies such as medical expenses or urgent repairs.",
      interestRate: 10,
      interestType: "flat",
      minAmount: 10000,
      maxAmount: 500000,
      maxTermMonths: 6,
      processingFee: 5000,
      insuranceFee: 1,
      penaltyRate: 3,
      requiredSharesMultiplier: 2,
      requiredGuarantors: 1,
    },
  },
  {
    label: "School Fees Loan",
    values: {
      name: "School Fees Loan",
      code: "SFL-001",
      description:
        "Educational financing to cover tuition and related academic expenses for members and their dependents.",
      interestRate: 8,
      interestType: "reducing",
      minAmount: 50000,
      maxAmount: 3000000,
      maxTermMonths: 12,
      processingFee: 10000,
      insuranceFee: 1.5,
      penaltyRate: 2,
      requiredSharesMultiplier: 3,
      requiredGuarantors: 2,
    },
  },
  {
    label: "Business Loan",
    values: {
      name: "Business Loan",
      code: "BIZ-001",
      description:
        "Capital for small business development, expansion, or working capital needs.",
      interestRate: 12,
      interestType: "reducing",
      minAmount: 100000,
      maxAmount: 10000000,
      maxTermMonths: 24,
      processingFee: 25000,
      insuranceFee: 2,
      penaltyRate: 2.5,
      requiredSharesMultiplier: 3,
      requiredGuarantors: 2,
    },
  },
  {
    label: "Agriculture Loan",
    values: {
      name: "Agriculture Loan",
      code: "AGR-001",
      description:
        "Financing for farming activities including crop production, livestock, and agricultural equipment.",
      interestRate: 9,
      interestType: "reducing",
      minAmount: 50000,
      maxAmount: 5000000,
      maxTermMonths: 18,
      processingFee: 15000,
      insuranceFee: 1.5,
      penaltyRate: 2,
      requiredSharesMultiplier: 2,
      requiredGuarantors: 2,
    },
  },
];

const formatCurrency = (value: number): string => {
  if (isNaN(value)) return "0 FRW";
  return `${new Intl.NumberFormat("en-RW").format(Math.round(value))} FRW`;
};

export default function LoanProductForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);

  const [form, setForm] = useState<LoanTypeForm>({ ...defaultForm });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEditing);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (id) {
      loadLoanType(id);
    }
  }, [id]);

  const loadLoanType = async (loanId: string) => {
    setLoading(true);
    try {
      const res = await api.get(`/loan-types/${loanId}`);
      const lt = res.data?.data || {};
      setForm({
        id: lt.id,
        name: lt.name || "",
        code: lt.code || "",
        description: lt.description || "",
        interestRate: Number(lt.interestRate) || 12,
        interestType: lt.interestType || "flat",
        maxAmount: lt.maxAmount || 5000000,
        minAmount: lt.minAmount || 50000,
        maxTermMonths: lt.maxTermMonths || 12,
        processingFee: lt.processingFee || 0,
        insuranceFee: lt.insuranceFee || 0,
        penaltyRate: lt.penaltyRate || 2,
        requiredSharesMultiplier: lt.requiredSharesMultiplier || 2,
        requiredGuarantors: lt.requiredGuarantors || 2,
        isActive: lt.isActive !== false,
      });
    } catch {
      toast.error("Failed to load loan type");
      navigate("/settings");
    } finally {
      setLoading(false);
    }
  };

  const applyTemplate = (tpl: Template) => {
    setForm((prev) => ({ ...prev, ...tpl.values }));
    setErrors({});
    toast.success(`"${tpl.label}" template applied`);
  };

  const updateField = <K extends keyof LoanTypeForm>(
    key: K,
    value: LoanTypeForm[K]
  ) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key as string]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[key as string];
        return next;
      });
    }
  };

  const validate = (): boolean => {
    const errs: Record<string, string> = {};

    if (!form.name.trim()) errs.name = "Loan product name is required";
    if (!form.code.trim()) errs.code = "Product code is required";
    if (form.interestRate <= 0) errs.interestRate = "Interest rate must be greater than 0";
    if (form.minAmount <= 0) errs.minAmount = "Minimum amount must be greater than 0";
    if (form.maxAmount <= form.minAmount)
      errs.maxAmount = "Maximum amount must exceed minimum amount";
    if (form.maxTermMonths <= 0) errs.maxTermMonths = "Maximum term is required";
    if (form.processingFee < 0) errs.processingFee = "Processing fee cannot be negative";
    if (form.insuranceFee < 0) errs.insuranceFee = "Insurance fee cannot be negative";
    if (form.penaltyRate < 0) errs.penaltyRate = "Penalty rate cannot be negative";
    if (form.requiredSharesMultiplier <= 0)
      errs.requiredSharesMultiplier = "Shares multiplier must be at least 1";
    if (form.requiredGuarantors < 0)
      errs.requiredGuarantors = "Required guarantors cannot be negative";

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      toast.error("Please fix the form errors before continuing");
      return;
    }

    setSaving(true);
    try {
      if (isEditing) {
        await api.put(`/loan-types/${id}`, form);
        toast.success("Loan type updated successfully");
      } else {
        await api.post("/loan-types", form);
        toast.success("Loan type created successfully");
      }
      navigate("/settings#loanProducts");
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to save loan type";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-[#0A9F43]" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6 space-y-6">
      {/* Page Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/settings")}
          className="h-9 w-9 shrink-0"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-xl font-bold text-gray-900">
            {isEditing ? "Edit Loan Type" : "Create Loan Type"}
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {isEditing
              ? "Modify the configuration of this loan type."
              : "Configure a new loan type that members can apply for."}
          </p>
        </div>
      </div>

      <Separator />

      {/* Quick Templates */}
      <Card className="border rounded-md shadow-none">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-amber-500" />
            <CardTitle className="text-base">Quick Templates</CardTitle>
          </div>
          <CardDescription>
            Start from a predefined loan type template and customize as needed.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {TEMPLATES.map((tpl) => (
              <Button
                key={tpl.label}
                variant="outline"
                size="sm"
                onClick={() => applyTemplate(tpl)}
                className="border-gray-200 hover:border-[#0A9F43] hover:text-[#0A9F43] hover:bg-green-50 transition-colors"
              >
                <Sparkles className="h-3.5 w-3.5 mr-1.5 text-amber-400" />
                {tpl.label}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Section 1: Loan Product Information */}
      <Card className="border rounded-md shadow-none">
        <CardHeader className="pb-3 border-b bg-gray-50/50">
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-blue-600" />
            <CardTitle className="text-base">Loan Type Information</CardTitle>
          </div>
          <CardDescription>
            Create a loan type members can apply for.
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-4 space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-1.5">
              <Label className="text-sm font-medium text-gray-700">
                Loan Name <span className="text-red-500">*</span>
              </Label>
              <Input
                value={form.name}
                onChange={(e) => updateField("name", e.target.value)}
                placeholder="e.g. Emergency Loan"
                className={`h-10 text-sm ${errors.name ? "border-red-400" : ""}`}
              />
              {errors.name && (
                <p className="text-xs text-red-500">{errors.name}</p>
              )}
            </div>
            <div className="grid gap-1.5">
              <Label className="text-sm font-medium text-gray-700">
                Loan Type Code <span className="text-red-500">*</span>
              </Label>
              <Input
                value={form.code}
                onChange={(e) => updateField("code", e.target.value)}
                placeholder="e.g. EL-001"
                className={`h-10 text-sm ${errors.code ? "border-red-400" : ""}`}
              />
              {errors.code && (
                <p className="text-xs text-red-500">{errors.code}</p>
              )}
            </div>
          </div>
          <div className="grid gap-1.5">
            <Label className="text-sm font-medium text-gray-700">
              Description
            </Label>
            <Textarea
              value={form.description}
              onChange={(e) => updateField("description", e.target.value)}
              placeholder="Describe what this loan product offers and its purpose..."
              className="min-h-[80px] text-sm"
            />
          </div>
        </CardContent>
      </Card>

      {/* Section 2: Interest & Charges */}
      <Card className="border rounded-md shadow-none">
        <CardHeader className="pb-3 border-b bg-gray-50/50">
          <div className="flex items-center gap-2">
            <Percent className="h-5 w-5 text-emerald-600" />
            <CardTitle className="text-base">Interest & Charges</CardTitle>
          </div>
          <CardDescription>
            Configure interest rates and applicable fees for this loan type.
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-4 space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-1.5">
              <Label className="text-sm font-medium text-gray-700">
                Interest Rate (%) <span className="text-red-500">*</span>
              </Label>
              <div className="relative">
                <Percent className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="number"
                  value={form.interestRate}
                  onChange={(e) =>
                    updateField("interestRate", Number(e.target.value))
                  }
                  className={`h-10 text-sm pl-9 ${errors.interestRate ? "border-red-400" : ""}`}
                />
              </div>
              {errors.interestRate && (
                <p className="text-xs text-red-500">{errors.interestRate}</p>
              )}
            </div>
            <div className="grid gap-1.5">
              <Label className="text-sm font-medium text-gray-700">
                Interest Type <span className="text-red-500">*</span>
              </Label>
              <Select
                value={form.interestType}
                onValueChange={(v: "flat" | "reducing") =>
                  updateField("interestType", v)
                }
              >
                <SelectTrigger className="h-10 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="flat">Flat Rate</SelectItem>
                  <SelectItem value="reducing">Reducing Balance</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-1.5">
              <Label className="text-sm font-medium text-gray-700">
                Processing Fee (FRW)
              </Label>
              <Input
                type="number"
                value={form.processingFee}
                onChange={(e) =>
                  updateField("processingFee", Number(e.target.value))
                }
                className={`h-10 text-sm ${errors.processingFee ? "border-red-400" : ""}`}
              />
              {form.processingFee > 0 && (
                <p className="text-xs text-gray-400">
                  {formatCurrency(form.processingFee)}
                </p>
              )}
              {errors.processingFee && (
                <p className="text-xs text-red-500">{errors.processingFee}</p>
              )}
            </div>
            <div className="grid gap-1.5">
              <Label className="text-sm font-medium text-gray-700">
                Insurance Fee (%)
              </Label>
              <div className="relative">
                <Percent className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="number"
                  value={form.insuranceFee}
                  onChange={(e) =>
                    updateField("insuranceFee", Number(e.target.value))
                  }
                  className={`h-10 text-sm pl-9 ${errors.insuranceFee ? "border-red-400" : ""}`}
                  step="0.1"
                />
              </div>
              {errors.insuranceFee && (
                <p className="text-xs text-red-500">{errors.insuranceFee}</p>
              )}
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-1.5">
              <Label className="text-sm font-medium text-gray-700">
                Penalty Rate (%) <span className="text-red-500">*</span>
              </Label>
              <div className="relative">
                <Percent className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="number"
                  value={form.penaltyRate}
                  onChange={(e) =>
                    updateField("penaltyRate", Number(e.target.value))
                  }
                  className={`h-10 text-sm pl-9 ${errors.penaltyRate ? "border-red-400" : ""}`}
                  step="0.1"
                />
              </div>
              {errors.penaltyRate && (
                <p className="text-xs text-red-500">{errors.penaltyRate}</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Section 3: Loan Limits */}
      <Card className="border rounded-md shadow-none">
        <CardHeader className="pb-3 border-b bg-gray-50/50">
          <div className="flex items-center gap-2">
            <Wallet className="h-5 w-5 text-indigo-600" />
            <CardTitle className="text-base">Loan Limits</CardTitle>
          </div>
          <CardDescription>
            Set the borrowing range and repayment period for this loan type.
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-4 space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-1.5">
              <Label className="text-sm font-medium text-gray-700">
                Minimum Amount (FRW) <span className="text-red-500">*</span>
              </Label>
              <Input
                type="number"
                value={form.minAmount}
                onChange={(e) =>
                  updateField("minAmount", Number(e.target.value))
                }
                className={`h-10 text-sm ${errors.minAmount ? "border-red-400" : ""}`}
              />
              {form.minAmount > 0 && (
                <p className="text-xs text-gray-400">
                  {formatCurrency(form.minAmount)}
                </p>
              )}
              {errors.minAmount && (
                <p className="text-xs text-red-500">{errors.minAmount}</p>
              )}
            </div>
            <div className="grid gap-1.5">
              <Label className="text-sm font-medium text-gray-700">
                Maximum Amount (FRW) <span className="text-red-500">*</span>
              </Label>
              <Input
                type="number"
                value={form.maxAmount}
                onChange={(e) =>
                  updateField("maxAmount", Number(e.target.value))
                }
                className={`h-10 text-sm ${errors.maxAmount ? "border-red-400" : ""}`}
              />
              {form.maxAmount > 0 && (
                <p className="text-xs text-gray-400">
                  {formatCurrency(form.maxAmount)}
                </p>
              )}
              {errors.maxAmount && (
                <p className="text-xs text-red-500">{errors.maxAmount}</p>
              )}
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-1.5">
              <Label className="text-sm font-medium text-gray-700">
                Maximum Term (Months) <span className="text-red-500">*</span>
              </Label>
              <Input
                type="number"
                value={form.maxTermMonths}
                onChange={(e) =>
                  updateField("maxTermMonths", Number(e.target.value))
                }
                className={`h-10 text-sm ${errors.maxTermMonths ? "border-red-400" : ""}`}
              />
              {errors.maxTermMonths && (
                <p className="text-xs text-red-500">{errors.maxTermMonths}</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Section 4: Eligibility Requirements */}
      <Card className="border rounded-md shadow-none">
        <CardHeader className="pb-3 border-b bg-gray-50/50">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-purple-600" />
            <CardTitle className="text-base">Eligibility Requirements</CardTitle>
          </div>
          <CardDescription>
            Configure member eligibility criteria for this loan type.
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-4 space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-1.5">
              <Label className="text-sm font-medium text-gray-700">
                Required Shares Multiplier <span className="text-red-500">*</span>
              </Label>
              <Input
                type="number"
                value={form.requiredSharesMultiplier}
                onChange={(e) =>
                  updateField(
                    "requiredSharesMultiplier",
                    Number(e.target.value)
                  )
                }
                className={`h-10 text-sm ${errors.requiredSharesMultiplier ? "border-red-400" : ""}`}
              />
              <p className="text-xs text-gray-400">
                Members must have savings equal to {form.requiredSharesMultiplier}x the requested loan.
              </p>
              {errors.requiredSharesMultiplier && (
                <p className="text-xs text-red-500">
                  {errors.requiredSharesMultiplier}
                </p>
              )}
            </div>
            <div className="grid gap-1.5">
              <Label className="text-sm font-medium text-gray-700">
                Required Guarantors
              </Label>
              <Input
                type="number"
                value={form.requiredGuarantors}
                onChange={(e) =>
                  updateField("requiredGuarantors", Number(e.target.value))
                }
                className={`h-10 text-sm ${errors.requiredGuarantors ? "border-red-400" : ""}`}
              />
              {errors.requiredGuarantors && (
                <p className="text-xs text-red-500">
                  {errors.requiredGuarantors}
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Section 5: Product Status */}
      <Card className="border rounded-md shadow-none">
        <CardHeader className="pb-3 border-b bg-gray-50/50">
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-gray-600" />
            <CardTitle className="text-base">Product Status</CardTitle>
          </div>
          <CardDescription>
            Control whether this product is available for member applications.
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="flex items-center justify-between py-2">
            <div>
              <Label
                htmlFor="product-status"
                className="text-sm font-medium text-gray-700 cursor-pointer"
              >
                Active Product
              </Label>
              <p className="text-xs text-gray-400">
                {form.isActive
                  ? "Product is available for members to apply."
                  : "When OFF, product cannot be applied for."}
              </p>
            </div>
            <Switch
              id="product-status"
              checked={form.isActive}
              onCheckedChange={(v) => updateField("isActive", v)}
              className="h-7 w-[52px] data-[state=checked]:bg-[#16A34A] data-[state=unchecked]:bg-[#E5E7EB] data-[state=checked]:border-[#16A34A] data-[state=unchecked]:border-[#CBD5E1] hover:data-[state=checked]:bg-[#16A34A]/90 hover:data-[state=unchecked]:bg-[#D1D5DB] focus-visible:ring-[rgba(22,163,74,0.25)]"
              thumbClassName="size-[22px] bg-white shadow-md shadow-black/25 data-[state=checked]:translate-x-[26px] data-[state=unchecked]:translate-x-0"
            />
          </div>
        </CardContent>
      </Card>

      {/* Sticky Footer */}
      <div className="sticky bottom-0 bg-white border-t rounded-b-lg px-6 py-4 flex items-center justify-end gap-3 -mx-6 -mb-6 mt-8">
        <Button
          variant="outline"
          onClick={() => navigate("/settings")}
          className="h-10 px-6"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={saving}
          className="h-10 px-8 bg-[#0A9F43] hover:bg-[#0A9F43]/90 text-white font-medium min-w-[180px]"
        >
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              {isEditing ? "Updating Loan Type..." : "Creating Loan Type..."}
            </>
          ) : (
            <>{isEditing ? "Update Loan Type" : "Create Loan Type"}</>
          )}
        </Button>
      </div>
    </div>
  );
}
